import { IconCheckCircle } from "@/assets/icons/geist/IconCheckCircle";
import { IconPlaceholder } from "@/assets/icons/geist/IconPlaceholder";
import { IconPlay } from "@/assets/icons/geist/IconPlay";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { IconWarning } from "@/assets/icons/geist/IconWarning";
import { EnvironmentDetailsEmptySection } from "@/components/environments/EnvironmentDetailsEmptySection";
import { Button } from "@/components/flexkit/Button";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading3 } from "@/components/podkit/typography/Headings";
import { timeAgo } from "@/format/time";
import {
    useListTaskExecutions,
    useListTasks,
    useStartTask,
    type PlainTask,
    type PlainTaskExecution,
} from "@/queries/automations-queries";
import { useEnvironment } from "@/queries/environment-queries";
import { canOpen } from "@/routes/environments/phase";
import { formatError } from "@/utils/errors";
import { Timestamp } from "@bufbuild/protobuf";
import { TaskExecutionPhase } from "gitpod-next-api/gitpod/v1/environment_automation_pb";
import { EnvironmentStatus_ContentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { useCallback, useMemo, type FC } from "react";
import { useNavigate } from "react-router-dom";

export const EnvironmentTasks: FC<{ environmentId: string }> = ({ environmentId }) => {
    const { data: environment } = useEnvironment(environmentId);
    const { data: tasks, isPending, error } = useListTasks(environmentId);
    const { data: taskRuns } = useListTaskExecutions(environmentId);
    const ready = (!isPending && tasks !== undefined) || Boolean(error);

    const disabled = !environment || !canOpen(environment);

    const navigate = useNavigate();

    const onClick = useCallback(() => {
        navigate(`/details/${environmentId}/tasks`);
    }, [environmentId, navigate]);

    const count = tasks ? <span className="font-light">({tasks.length})</span> : "";

    let emptyState: "info" | "skeleton" = "info";
    if (
        environment?.spec?.automationsFile &&
        (environment.status?.automationsFile?.phase === EnvironmentStatus_ContentPhase.INITIALIZING ||
            environment.status?.automationsFile?.phase === EnvironmentStatus_ContentPhase.CREATING ||
            environment.status?.automationsFile?.phase === EnvironmentStatus_ContentPhase.UPDATING)
    ) {
        emptyState = "skeleton";
    }

    return (
        <div>
            <div className="flex items-center justify-between pb-6">
                <Heading3 className="select-none text-lg font-bold text-content-primary">Tasks {count}</Heading3>

                <Button
                    disabled={disabled || !tasks?.length}
                    className={tasks?.length ? "" : "opacity-0"}
                    onClick={onClick}
                    variant="secondary"
                    data-track-label="true"
                >
                    View Task Runs
                </Button>
            </div>
            <SkeletonBlock failed={Boolean(error)} ready={ready} className="h-20">
                <TasksContainer
                    tasks={tasks || []}
                    taskRuns={taskRuns || []}
                    disabled={disabled}
                    emptyState={emptyState}
                />
            </SkeletonBlock>
        </div>
    );
};

const filterManualTasks = (tasks: PlainTask[]) => {
    return tasks.filter((t) => t.metadata?.triggeredBy.some((t) => t.trigger.case === "manual"));
};
const filterNotManualTasks = (tasks: PlainTask[]) => {
    return tasks.filter((t) => !t.metadata?.triggeredBy.some((t) => t.trigger.case === "manual"));
};

const TasksContainer: FC<{
    tasks: PlainTask[];
    taskRuns: PlainTaskExecution[];
    disabled?: boolean;
    emptyState: "info" | "skeleton";
}> = ({ tasks, taskRuns, disabled, emptyState }) => {
    const manualTasks = useMemo(() => filterManualTasks(tasks || []), [tasks]);
    const notManualTasks = useMemo(() => filterNotManualTasks(tasks || []), [tasks]);

    if (tasks.length === 0) {
        if (emptyState === "skeleton") {
            return <SkeletonBlock failed={false} ready={false} className="h-20" />;
        }

        return (
            <EnvironmentDetailsEmptySection
                icon={<IconPlaceholder kind="automations" />}
                title="Add tasks to your environment"
                text="You can add tasks by editing the .gitpod/automations.yaml file."
                docsUrl="https://www.gitpod.io/docs/flex/integrations/ports"
            />
        );
    }

    return (
        <>
            {manualTasks.length > 0 && <TaskCards disabled={disabled} tasks={manualTasks} runs={taskRuns || []} />}
            {notManualTasks.length > 0 && (
                <TasksTable disabled={disabled} tasks={notManualTasks} runs={taskRuns || []} />
            )}
        </>
    );
};

const TaskCards: FC<{ tasks: PlainTask[]; runs: PlainTaskExecution[]; disabled?: boolean }> = ({
    tasks,
    runs,
    disabled,
}) => {
    if (tasks.length === 0) {
        return <></>;
    }

    return (
        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {tasks.map((task) => (
                <TaskCard
                    key={task.id}
                    task={task}
                    runs={runs.filter((r) => r.metadata?.taskId === task.id)}
                    disabled={disabled}
                />
            ))}
        </div>
    );
};

const TaskCard: FC<{ task: PlainTask; runs: PlainTaskExecution[]; disabled?: boolean }> = ({
    task,
    runs,
    disabled,
}) => {
    const navigate = useNavigate();

    const startTask = useStartTask();
    const { toast } = useToast();
    const lastRun = useMemo(() => {
        return runs
            .filter((r) => r.metadata?.taskId === task.id)
            .sort((a, b) => Number(a?.metadata?.createdAt?.seconds || 0) - Number(b?.metadata?.createdAt?.seconds || 0))
            .pop();
    }, [runs, task.id]);
    const lastRunTimestamp = lastRun?.metadata?.createdAt;
    const lastRunTime = lastRunTimestamp && timeAgo(new Timestamp(lastRunTimestamp).toDate());

    const onRunTask = useCallback(async () => {
        try {
            const run = await startTask.mutateAsync(task.id);
            const runLog = `/details/${run.metadata?.environmentId}/task/${run.metadata?.taskId}/run/${run.id}`;
            const handle = toast({
                title: <>Task started</>,
                action: (
                    <Button
                        onClick={() => {
                            navigate(runLog);
                            handle.dismiss();
                        }}
                        data-track-label="true"
                    >
                        View Logs
                    </Button>
                ),
            });
        } catch (error) {
            toast({ title: "Failed to start task", description: formatError(error) });
        }
    }, [navigate, startTask, task.id, toast]);

    const runLog = `/details/${lastRun?.metadata?.environmentId}/task/${lastRun?.metadata?.taskId}/run/${lastRun?.id}`;
    return (
        <div className="group flex flex-col gap-8 truncate rounded-xl border border-border-base bg-surface-glass p-4 hover:shadow">
            <div className="flex w-full flex-row items-center">
                <div className="flex flex-row gap-2">
                    <Button
                        LeadingIcon={IconPlay}
                        disabled={disabled}
                        loading={startTask.isPending}
                        size="sm"
                        variant="secondary"
                        onClick={async () => {
                            await onRunTask();
                        }}
                        data-track-label="true"
                    />
                    <Button
                        size="sm"
                        variant="secondary"
                        className="opacity-0 group-hover:opacity-100"
                        disabled={disabled || !lastRun}
                        onClick={() => {
                            navigate(runLog);
                        }}
                        data-track-label="true"
                    >
                        Logs
                    </Button>
                </div>
                <div className="flex grow flex-row items-center justify-end gap-2">
                    <div className="flex items-center justify-center">{phaseIcon(lastRun?.status?.phase)}</div>
                    <div className="text-end text-sm text-content-tertiary">{lastRunTime || ""}</div>
                </div>
            </div>
            <div className="flex w-full grow flex-row items-start gap-2">
                <div className="flex grow flex-col truncate">
                    <div className="truncate text-base font-bold text-content-primary">{task.metadata?.name}</div>
                    <div className="line-clamp-2 truncate text-wrap text-sm text-content-secondary">
                        {task.metadata?.description}
                    </div>
                </div>
            </div>
        </div>
    );
};

const TasksTable: FC<{ tasks: PlainTask[]; runs: PlainTaskExecution[]; disabled?: boolean }> = ({
    tasks,
    runs,
    disabled,
}) => {
    return (
        <div className="flex w-full flex-col gap-0.5">
            <TaskHeaderRow />
            {tasks.map((task) => (
                <TaskRow
                    key={task.id}
                    disabled={disabled}
                    task={task}
                    runs={runs.filter((r) => r.metadata?.taskId === task.id)}
                />
            ))}
        </div>
    );
};

const TaskHeaderRow: FC = () => {
    return (
        <>
            <div className="group inline-flex h-9 items-center justify-start gap-4 border-b border-border-base px-4 py-2.5 text-base font-bold text-content-secondary">
                <div className="min-w-5/12 max-w-5/12 flex w-5/12 flex-col truncate">Task</div>
                <div className="max-w-3/12 flex h-5 w-4/12 items-center justify-start truncate">Trigger</div>
                <div className="flex h-5 w-2/12 min-w-20 items-center justify-start gap-2">Status</div>
                <div className="flex w-12 min-w-12 items-center justify-end gap-2 opacity-0" />
            </div>
        </>
    );
};
const TaskRow: FC<{ task: PlainTask; runs: PlainTaskExecution[]; disabled?: boolean }> = ({ task, runs, disabled }) => {
    const lastRun = useMemo(() => {
        return runs
            .filter((r) => r.metadata?.taskId === task.id)
            .sort((a, b) => Number(a?.metadata?.createdAt?.seconds || 0) - Number(b?.metadata?.createdAt?.seconds || 0))
            .pop();
    }, [runs, task.id]);
    const lastRunTimestamp = lastRun?.metadata?.createdAt;
    const lastRunTime = lastRunTimestamp && timeAgo(new Timestamp(lastRunTimestamp).toDate());

    const triggers = (task.metadata?.triggeredBy || []).map((t) => t.trigger.case);

    const navigate = useNavigate();
    const onViewLastTaskRunLogs = useCallback(() => {
        navigate(`/details/${task.environmentId}/task/${task.id}/run/${lastRun?.id}`);
    }, [lastRun?.id, navigate, task.environmentId, task.id]);

    return (
        <>
            <div className="group inline-flex h-[52px] items-center justify-start gap-4 border-b border-border-base px-4 py-2.5 text-base">
                <div className="min-w-5/12 max-w-5/12 flex w-5/12 flex-col truncate">
                    <div className="truncate font-bold text-content-primary">{task.metadata?.name}</div>
                    <div className="truncate text-sm text-content-secondary">{task.metadata?.description}</div>
                </div>
                <div className="max-w-3/12 flex h-5 w-4/12 items-center justify-start truncate">
                    <span className="truncate">{triggers.map((t) => nicerTriggerName(t)).join(", ")}</span>
                </div>
                <div className="flex h-5 w-2/12 min-w-20 items-center justify-start gap-2">
                    <div className="flex items-center justify-center">{phaseIcon(lastRun?.status?.phase)}</div>
                    <div className="text-content-primary">{lastRunTime}</div>
                </div>
                <div className="flex w-12 min-w-12 items-center justify-end gap-2 opacity-0 group-hover:opacity-100">
                    <Button
                        size="md"
                        variant="secondary"
                        className="font-bold leading-none"
                        disabled={disabled || !lastRun}
                        onClick={onViewLastTaskRunLogs}
                        data-track-label="true"
                    >
                        Logs
                    </Button>
                </div>
            </div>
        </>
    );
};

function nicerTriggerName(trigger: string | undefined) {
    switch (trigger) {
        case "manual":
            return "Manual";
        case "postDevcontainerStart":
            return "Post Devcontainer Start";
        case "postEnvironmentStart":
            return "Post Environment Start";
    }
    return trigger;
}

function phaseIcon(phase?: TaskExecutionPhase) {
    switch (phase) {
        case TaskExecutionPhase.SUCCEEDED:
            return <IconCheckCircle size="sm" className="text-content-green" />;
        case TaskExecutionPhase.FAILED:
            return <IconWarning size="sm" className="text-content-negative" />;
        case TaskExecutionPhase.RUNNING:
            return <IconSpinner size="sm" className="animate-spin text-content-yield" />;
        case TaskExecutionPhase.PENDING:
            return <IconSpinner size="sm" className="animate-spin" />;
        default:
            return;
    }
}
